import type { GetBadgeResp } from "@/types/accounts.ts";
import { api } from "@/lib/http.ts";
import { isSharedThreadPath } from "@/lib/sharedThread.ts";
import { queryOptions, useQuery } from "@tanstack/react-query";

/** Query factory for the awareness badge. */
export const badgeQueries = {
  all: () => ["badge"] as const,
  detail: () =>
    queryOptions({
      queryKey: [...badgeQueries.all(), "detail"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<GetBadgeResp>("/api/a/users/me/badge", { signal });
        return response.data;
      },
      // Short enough that returning to the tab re-reads the count: React Query's
      // focus refetch fires only on a stale query, and the socket triggers can't
      // cover a transition whose write emits no thread event. Not a staleness
      // bound — nothing re-reads while the tab stays focused. `invalidateQueries`
      // refetches active observers regardless, so the socket path is still
      // bounded by its own throttle rather than by this.
      staleTime: 60 * 1000,
      // Shared thread views are unauthenticated; the endpoint 401s there. Uses
      // the non-hook check since this can run before the Router mounts.
      enabled: !isSharedThreadPath(),
    }),
};

export type UseBadgeResult = {
  badge: GetBadgeResp | undefined;
  isPending: boolean;
  error: Error | null;
};

/**
 * The awareness badge: needs-attention threads plus completions unseen since the
 * user last viewed the threads list. Its dedicated endpoint keeps frequent
 * socket-driven refreshes independent from the full user-context payload.
 *
 * Exposes the query state so consumers can distinguish unavailable data from a
 * successful zero-count response. Authentication failures are handled by the
 * shared API client.
 */
export const useBadge = (): UseBadgeResult => {
  const { data, isPending, error } = useQuery(badgeQueries.detail());
  return { badge: data, isPending, error };
};
